/** Client-side PDF split / merge / page images via pdf-lib + pdf.js. */

import { PDFDocument } from "pdf-lib";
import * as pdfjs from "pdfjs-dist/legacy/build/pdf.mjs";
import { cloneArrayBuffer } from "./file";
import {
  configurePdfJsWorker,
  pdfCMapUrl,
  pdfStandardFontDataUrl,
  preloadPdfRuntime,
} from "./pdfAssets";

let workerConfigured = false;

function ensureWorker(): void {
  if (workerConfigured) return;
  configurePdfJsWorker(pdfjs);
  workerConfigured = true;
}

export async function splitPdfPages(
  source: ArrayBuffer,
  pageNumbers: number[]
): Promise<Uint8Array> {
  const src = await PDFDocument.load(source, { ignoreEncryption: true });
  const out = await PDFDocument.create();
  const indices = pageNumbers
    .map((n) => n - 1)
    .filter((i) => i >= 0 && i < src.getPageCount());
  if (!indices.length) {
    throw new Error("صفحه‌ای برای تفکیک انتخاب نشده است.");
  }
  const pages = await out.copyPages(src, indices);
  for (const p of pages) out.addPage(p);
  return out.save();
}

/** Split a contiguous inclusive range (1-based). */
export async function splitPdfRange(
  source: ArrayBuffer,
  fromPage: number,
  toPage: number
): Promise<Uint8Array> {
  const from = Math.min(fromPage, toPage);
  const to = Math.max(fromPage, toPage);
  const nums: number[] = [];
  for (let n = from; n <= to; n++) nums.push(n);
  return splitPdfPages(source, nums);
}

export async function mergePdfs(
  buffers: ArrayBuffer[],
  order: number[]
): Promise<Uint8Array> {
  const out = await PDFDocument.create();
  for (const idx of order) {
    const buf = buffers[idx];
    if (!buf) continue;
    const doc = await PDFDocument.load(buf, { ignoreEncryption: true });
    const pages = await out.copyPages(doc, doc.getPageIndices());
    for (const p of pages) out.addPage(p);
  }
  if (out.getPageCount() === 0) {
    throw new Error("فایلی برای ادغام وجود ندارد.");
  }
  return out.save();
}

export async function pdfPageCount(buffer: ArrayBuffer): Promise<number> {
  const doc = await PDFDocument.load(buffer, { ignoreEncryption: true });
  return doc.getPageCount();
}

export type PdfThumb = { dataUrl: string; width: number; height: number };

async function renderPageToThumb(
  page: pdfjs.PDFPageProxy,
  maxWidth: number
): Promise<PdfThumb | null> {
  const base = page.getViewport({ scale: 1 });
  const scale = maxWidth / base.width;
  const viewport = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(viewport.width);
  canvas.height = Math.ceil(viewport.height);
  const ctx = canvas.getContext("2d", { alpha: false });
  if (!ctx) return null;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  await page.render({
    canvasContext: ctx,
    viewport,
    intent: "display",
  }).promise;
  return {
    dataUrl: canvas.toDataURL("image/jpeg", 0.72),
    width: canvas.width,
    height: canvas.height,
  };
}

/** Render page thumbnail as a small JPEG data URL. */
export async function renderPdfPageThumb(
  buffer: ArrayBuffer,
  pageNumber = 1,
  maxWidth = 96
): Promise<PdfThumb | null> {
  try {
    ensureWorker();
    await preloadPdfRuntime();
    const data = new Uint8Array(cloneArrayBuffer(buffer));
    const task = pdfjs.getDocument({
      data,
      cMapUrl: pdfCMapUrl(),
      cMapPacked: true,
      standardFontDataUrl: pdfStandardFontDataUrl(),
      isEvalSupported: false,
      useSystemFonts: false,
      disableFontFace: true,
      verbosity: 0,
    });
    const pdf = await task.promise;
    try {
      const page = await pdf.getPage(
        Math.min(Math.max(1, pageNumber), pdf.numPages)
      );
      return await renderPageToThumb(page, maxWidth);
    } finally {
      await pdf.destroy();
    }
  } catch {
    return null;
  }
}

/**
 * Render every page thumbnail once (single PDF load).
 * Calls `onPage` progressively so the UI can show thumbs as they arrive.
 */
export async function renderAllPdfPageThumbs(
  buffer: ArrayBuffer,
  maxWidth = 160,
  onPage?: (pageNumber: number, thumb: PdfThumb | null, total: number) => void
): Promise<(PdfThumb | null)[]> {
  ensureWorker();
  await preloadPdfRuntime();
  const data = new Uint8Array(cloneArrayBuffer(buffer));
  const pdf = await pdfjs.getDocument({
    data,
    cMapUrl: pdfCMapUrl(),
    cMapPacked: true,
    standardFontDataUrl: pdfStandardFontDataUrl(),
    isEvalSupported: false,
    useSystemFonts: false,
    disableFontFace: true,
    verbosity: 0,
  }).promise;

  const total = pdf.numPages;
  const results: (PdfThumb | null)[] = new Array(total).fill(null);
  try {
    for (let n = 1; n <= total; n++) {
      try {
        const page = await pdf.getPage(n);
        const thumb = await renderPageToThumb(page, maxWidth);
        results[n - 1] = thumb;
        onPage?.(n, thumb, total);
      } catch {
        results[n - 1] = null;
        onPage?.(n, null, total);
      }
    }
  } finally {
    await pdf.destroy();
  }
  return results;
}

/** Inclusive page ranges from split-after markers (1-based page indices). */
export function segmentsFromSplitAfter(
  totalPages: number,
  splitsAfter: Iterable<number>
): Array<{ from: number; to: number }> {
  const total = Math.max(1, totalPages);
  const cuts = [...new Set(splitsAfter)]
    .filter((n) => Number.isFinite(n) && n >= 1 && n < total)
    .sort((a, b) => a - b);
  const segs: Array<{ from: number; to: number }> = [];
  let start = 1;
  for (const cut of cuts) {
    segs.push({ from: start, to: cut });
    start = cut + 1;
  }
  segs.push({ from: start, to: total });
  return segs;
}

/**
 * Split segments over a (possibly reordered) page list.
 * `splitsAfterPages` are original 1-based page numbers: cut after that page
 * wherever it sits in `pageOrder`.
 */
export function segmentsFromPageOrder(
  pageOrder: number[],
  splitsAfterPages: Iterable<number>
): Array<{ pages: number[] }> {
  if (!pageOrder.length) return [];
  const cuts = new Set(
    [...splitsAfterPages].filter((n) => Number.isFinite(n))
  );
  const segs: Array<{ pages: number[] }> = [];
  let current: number[] = [];
  for (let i = 0; i < pageOrder.length; i++) {
    const page = pageOrder[i]!;
    current.push(page);
    const isLast = i === pageOrder.length - 1;
    if (!isLast && cuts.has(page)) {
      segs.push({ pages: current });
      current = [];
    }
  }
  if (current.length) segs.push({ pages: current });
  return segs;
}

export function identityPageOrder(pageCount: number): number[] {
  const n = Math.max(0, pageCount);
  return Array.from({ length: n }, (_, i) => i + 1);
}

export function pdfBaseName(fileName: string): string {
  const trimmed = fileName.trim() || "document";
  return trimmed.replace(/\.pdf$/i, "") || "document";
}

export function splitPartFileName(baseName: string, partIndex: number): string {
  return `${pdfBaseName(baseName)}-part-${partIndex}.pdf`;
}

export type ImageExportFormat = "image/jpeg" | "image/png";

export type PageImageResult = {
  page: number;
  bytes: Uint8Array;
  mime: ImageExportFormat;
  fileName: string;
};

/**
 * Export selected PDF pages as JPG/PNG.
 * quality: 0.4–1 for JPEG; scaleFactor controls pixel size (0.5–3).
 */
export async function exportPdfPagesAsImages(
  buffer: ArrayBuffer,
  pages: number[],
  opts: {
    format: ImageExportFormat;
    quality?: number;
    scaleFactor?: number;
    namePrefix?: string;
  }
): Promise<PageImageResult[]> {
  ensureWorker();
  await preloadPdfRuntime();
  const data = new Uint8Array(cloneArrayBuffer(buffer));
  const pdf = await pdfjs.getDocument({
    data,
    cMapUrl: pdfCMapUrl(),
    cMapPacked: true,
    standardFontDataUrl: pdfStandardFontDataUrl(),
    isEvalSupported: false,
    useSystemFonts: false,
    disableFontFace: true,
    verbosity: 0,
  }).promise;

  const scale = Math.max(0.5, Math.min(3, opts.scaleFactor ?? 1.5));
  const quality = Math.max(0.4, Math.min(1, opts.quality ?? 0.85));
  const ext = opts.format === "image/png" ? "png" : "jpg";
  const prefix = opts.namePrefix || "nevisign-page";
  const results: PageImageResult[] = [];

  try {
    for (const pageNum of pages) {
      if (pageNum < 1 || pageNum > pdf.numPages) continue;
      const page = await pdf.getPage(pageNum);
      const viewport = page.getViewport({ scale });
      const canvas = document.createElement("canvas");
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      const ctx = canvas.getContext("2d");
      if (!ctx) continue;
      await page.render({ canvasContext: ctx, viewport }).promise;
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (b) => resolve(b),
          opts.format,
          opts.format === "image/jpeg" ? quality : undefined
        );
      });
      if (!blob) continue;
      const ab = await blob.arrayBuffer();
      results.push({
        page: pageNum,
        bytes: new Uint8Array(ab),
        mime: opts.format,
        fileName: `${prefix}-${pageNum}.${ext}`,
      });
    }
  } finally {
    await pdf.destroy();
  }

  if (!results.length) {
    throw new Error("هیچ صفحه‌ای به تصویر تبدیل نشد.");
  }
  return results;
}
